<script setup lang="ts">
import { ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useFeatureFlag } from '@/composables/useFeatureFlag';

const router = useRouter();
const authStore = useAuthStore();
const isMenuOpen = ref(false);
const familyTreeEnabled = useFeatureFlag('family-tree');

async function handleLogout() {
  await authStore.logout();
  router.push('/login');
}
</script>

<template>
  <nav class="bg-sigmachi-blue shadow-lg">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <!-- Logo and primary nav -->
        <div class="flex">
          <div class="flex-shrink-0 flex items-center">
            <RouterLink to="/" class="flex items-center">
              <img src="/logo.png" alt="Eta Iota" class="h-12 w-auto" />
            </RouterLink>
          </div>

          <!-- Desktop nav -->
          <div class="hidden sm:ml-6 sm:flex sm:space-x-4" v-if="authStore.isAuthenticated">
            <RouterLink
              to="/directory"
              class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
              active-class="bg-blue-800"
            >
              Directory
            </RouterLink>

            <RouterLink
              to="/my-era"
              class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
              active-class="bg-blue-800"
            >
              My Era
            </RouterLink>

            <RouterLink
              v-if="familyTreeEnabled"
              to="/family-tree"
              class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
              active-class="bg-blue-800"
            >
              Family Tree <span class="text-xs opacity-75">(beta)</span>
            </RouterLink>

            <RouterLink
              to="/officers"
              class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
              active-class="bg-blue-800"
            >
              Officers
            </RouterLink>

            <RouterLink
              to="/profile"
              class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
              active-class="bg-blue-800"
            >
              My Profile
            </RouterLink>

            <template v-if="authStore.isAdmin">
              <RouterLink
                to="/admin"
                class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
                active-class="bg-blue-800"
              >
                Admin
              </RouterLink>
              <RouterLink
                to="/admin/guide"
                class="text-white hover:bg-blue-700 px-2 py-2 rounded-md"
                active-class="bg-blue-800"
                title="Admin Guide"
              >
                <span class="inline-flex items-center justify-center h-5 w-5 rounded-full border border-white text-xs font-bold leading-none">?</span>
              </RouterLink>
            </template>
          </div>
        </div>

        <!-- Right side -->
        <div class="hidden sm:ml-6 sm:flex sm:items-center">
          <template v-if="authStore.isAuthenticated">
            <span class="text-gray-300 text-sm mr-4">
              {{ authStore.user?.email }}
            </span>
            <button
              @click="handleLogout"
              class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
            >
              Sign Out
            </button>
          </template>

          <template v-else>
            <RouterLink
              to="/login"
              class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
            >
              Sign In
            </RouterLink>
          </template>
        </div>

        <!-- Mobile menu button -->
        <div class="flex items-center sm:hidden">
          <button
            @click="isMenuOpen = !isMenuOpen"
            class="text-white hover:bg-blue-700 p-2 rounded-md"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                v-if="!isMenuOpen"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
              <path
                v-else
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <div v-show="isMenuOpen" class="sm:hidden">
      <div class="pt-2 pb-3 space-y-1" v-if="authStore.isAuthenticated">
        <RouterLink
          to="/directory"
          class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
          @click="isMenuOpen = false"
        >
          Directory
        </RouterLink>

        <RouterLink
          to="/my-era"
          class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
          @click="isMenuOpen = false"
        >
          My Era
        </RouterLink>

        <RouterLink
          v-if="familyTreeEnabled"
          to="/family-tree"
          class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
          @click="isMenuOpen = false"
        >
          Family Tree <span class="text-xs opacity-75">(beta)</span>
        </RouterLink>

        <RouterLink
          to="/officers"
          class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
          @click="isMenuOpen = false"
        >
          Officers
        </RouterLink>

        <RouterLink
          to="/profile"
          class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
          @click="isMenuOpen = false"
        >
          My Profile
        </RouterLink>

        <template v-if="authStore.isAdmin">
          <RouterLink
            to="/admin"
            class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
            @click="isMenuOpen = false"
          >
            Admin
          </RouterLink>
          <RouterLink
            to="/admin/guide"
            class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
            @click="isMenuOpen = false"
          >
            Admin Guide
          </RouterLink>
        </template>

        <div class="border-t border-blue-700 pt-2 mt-2">
          <span class="text-gray-300 block px-3 py-2 text-sm">
            {{ authStore.user?.email }}
          </span>
          <button
            @click="handleLogout(); isMenuOpen = false"
            class="text-white hover:bg-blue-700 block w-full text-left px-3 py-2 text-base font-medium"
          >
            Sign Out
          </button>
        </div>
      </div>

      <div v-else class="pt-2 pb-3 space-y-1">
        <RouterLink
          to="/login"
          class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
          @click="isMenuOpen = false"
        >
          Sign In
        </RouterLink>
      </div>
    </div>
  </nav>
</template>
